// An honest statement, at the top of a Studio Operate screen, of whether this company's customer flow is wired
// into that screen — so an empty list is never read as "the feature works and nothing happened".
//
// Driven only by the application catalog (catalog.companyCapability). For a company without a catalog
// ({ known: false }) it renders nothing: we make no claim we cannot back.
import React from 'react';

const flowLabel = (apps) => apps
  .map((a) => `${a.displayName} (${a.route}${a.runtime && a.runtime.flowId ? `, flow ${a.runtime.flowId}` : ''})`)
  .join(', ');

const COPY = {
  approvals: {
    off: (apps) => `Not configured for this company: ${flowLabel(apps)} has no human-approval step, so it creates no approvals. Anything listed here came from other flows.`,
  },
  liveHandoff: {
    off: (apps) => `Not configured for this company: ${flowLabel(apps)} has no live-handoff step, so no customer conversations are routed here.`,
  },
  leads: {
    on: (apps, cap) => `Records here come from the public key of ${flowLabel(apps)} — any client using that key — plus Studio test and demo sessions, which are labelled on each lead.${
      cap.notRecorded && cap.notRecorded.length
        ? ` ${cap.notRecorded.map((a) => `${a.displayName} (${a.route})`).join(', ')} runs on the agents service and is not recorded here.`
        : ''}`,
  },
  analytics: {
    on: (apps, cap) => `Counts include every session on ${flowLabel(apps)} — Studio test and demo sessions too. A chat the visitor simply leaves stays "active": there is no idle timeout.${
      cap.notRecorded && cap.notRecorded.length
        ? ` ${cap.notRecorded.map((a) => `${a.displayName} (${a.route})`).join(', ')} is not counted.`
        : ''}`,
  },
};

export default function CapabilityNotice({ capability, kind }) {
  if (!capability || !capability.known) return null;
  const copy = COPY[kind];
  if (!copy) return null;
  const text = capability.enabled
    ? (copy.on ? copy.on(capability.enabledIn, capability) : null)
    : (copy.off ? copy.off(capability.apps, capability) : null);
  if (!text) return null;
  const tone = capability.enabled
    ? 'border-slate-200 bg-slate-50 text-slate-600'
    : 'border-amber-200 bg-amber-50 text-amber-800';
  return (
    <div role="note" data-testid={`capability-${kind}`} className={`mb-4 rounded-lg border px-3 py-2 text-[13px] ${tone}`}>
      {text}
    </div>
  );
}
